/**
 * TanStack Query hooks for Account Management.
 *
 * Response types are co-located here for the UI layer to import.
 * Mutations invalidate accountKeys — toast is handled by the UI layer, not here.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accountKeys } from "@/lib/query";
import type {
  AccountConstraintsGrouped,
  AccountDetail,
  AccountListPage,
  ApplyTierResponse,
  ExpectationLevelUpdateResponse,
  TierImpactResponse,
} from "@/schemas/accounts";

// Re-export response types so the UI layer only needs one import.
export type {
  AccountConstraintsGrouped,
  AccountDetail,
  AccountListPage,
  AccountRow,
  ApplyTierResponse,
  ExpectationLevelUpdateResponse,
  TierImpactResponse,
} from "@/schemas/accounts";

// ── Fetch helper ──────────────────────────────────────────────────────────────

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// ── Query param types ─────────────────────────────────────────────────────────

export interface AccountListParams {
  cursor?: string;
  direction?: "next" | "prev";
  search?: string;
  limit?: number;
}

// ── Query hooks ───────────────────────────────────────────────────────────────

export function useAccounts(params: AccountListParams = {}) {
  const search = new URLSearchParams();
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.direction) search.set("direction", params.direction);
  if (params.search) search.set("search", params.search);
  if (params.limit) search.set("limit", String(params.limit));
  const suffix = search.toString() ? `?${search.toString()}` : "";

  return useQuery({
    queryKey: accountKeys.list(params),
    queryFn: () => jsonFetch<AccountListPage>(`/api/accounts${suffix}`),
    placeholderData: keepPreviousData,
  });
}

export function useAccount(id: string, enabled = true) {
  return useQuery({
    queryKey: accountKeys.detail(id),
    queryFn: () => jsonFetch<AccountDetail>(`/api/accounts/${id}`),
    enabled,
  });
}

export function useTierImpact(accountId: string, tierId?: string, enabled = true) {
  const suffix = tierId ? `?tierId=${encodeURIComponent(tierId)}` : "";
  return useQuery({
    queryKey: accountKeys.tierImpact(accountId, tierId),
    queryFn: () => jsonFetch<TierImpactResponse>(`/api/accounts/${accountId}/tier-impact${suffix}`),
    enabled,
    staleTime: 0,
  });
}

export function useAccountConstraints(accountId: string, enabled = true) {
  return useQuery({
    queryKey: accountKeys.constraints(accountId),
    queryFn: () => jsonFetch<AccountConstraintsGrouped>(`/api/accounts/${accountId}/constraints`),
    enabled,
  });
}

/** A usable (active) constraint from the shared library, for the Account picker. */
export interface ConstraintLibraryItem {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  groupId: string;
  groupName: string;
}

/**
 * Active constraint library, read from the shared reference endpoint
 * (`/api/dashboard/reference`). Accessible to RM and ACCT — unlike the RM-only
 * admin constraint endpoints — so ACCT can populate the constraint picker.
 */
export function useConstraintLibrary(enabled = true) {
  return useQuery({
    queryKey: accountKeys.constraintLibrary(),
    queryFn: async () => {
      const ref = await jsonFetch<{ constraints?: ConstraintLibraryItem[] }>(
        "/api/dashboard/reference",
      );
      return ref.constraints ?? [];
    },
    enabled,
  });
}

// ── Mutation hooks ────────────────────────────────────────────────────────────

export interface PatchAccountPayload {
  name?: string;
  industry?: string | null;
  tierId?: string;
  externalNymblAccountId?: string | null;
}

export function usePatchAccount(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PatchAccountPayload) =>
      jsonFetch<{ id: string }>(`/api/accounts/${accountId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      qc.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

export function usePatchExpectationLevel(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (level: number) =>
      jsonFetch<ExpectationLevelUpdateResponse>(`/api/accounts/${accountId}/expectation-level`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ level }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      qc.invalidateQueries({ queryKey: accountKeys.all });
    },
  });
}

export function useApplyTierToSolutions(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (solutionIds: string[]) =>
      jsonFetch<ApplyTierResponse>(`/api/accounts/${accountId}/tier/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ solutionIds }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountKeys.tierImpact(accountId) });
      qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
      // Invalidate solution dashboards — tiers may have changed
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useAddAccountConstraint(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (constraintId: string) =>
      jsonFetch<{ id: string }>(`/api/accounts/${accountId}/constraints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ constraintId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountKeys.constraints(accountId) });
      qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
    },
  });
}

export function useRemoveAccountConstraint(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (constraintId: string) =>
      jsonFetch<{ ok: boolean }>(`/api/accounts/${accountId}/constraints/${constraintId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountKeys.constraints(accountId) });
      qc.invalidateQueries({ queryKey: accountKeys.detail(accountId) });
    },
  });
}
